import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ImageIcon, ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, waUrl } from "../data";

const GalleryItem = ({ item, i, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <motion.figure
      ref={ref}
      initial={{ opacity: 0, scale: 0.96 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
      onClick={() => item.src && onOpen(i)}
      className={`group relative overflow-hidden min-h-[240px] ${item.span} ${item.src ? "bg-muted cursor-pointer" : "bg-ink"}`}
      data-testid={`gallery-item-${i}`}
    >
      {item.src ? (
        <>
          <motion.img style={{ y }} src={item.src} alt={item.label} loading="lazy" decoding="async" className="absolute inset-0 w-full h-[124%] object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <span className="absolute top-4 right-4 grid place-items-center w-10 h-10 bg-white/90 text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ZoomIn size={18} />
          </span>
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center p-6">
          <div className="absolute inset-4 border border-dashed border-white/15" />
          <div className="relative text-center">
            <span className="mx-auto grid place-items-center w-12 h-12 border border-white/20 text-white/40 mb-4"><ImageIcon size={20} /></span>
            <div className="text-white/50 text-xs uppercase tracking-[0.2em]">Foto de trabajo real</div>
          </div>
        </div>
      )}
      <figcaption className={`absolute bottom-0 left-0 p-6 pointer-events-none ${item.src ? "translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100" : "opacity-100"} transition-[transform,opacity] duration-500`}>
        <span className="text-white font-heading font-semibold text-lg">{item.label}</span>
      </figcaption>
    </motion.figure>
  );
};

const Lightbox = ({ index, onClose, onNav }) => {
  const item = GALLERY[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-sm grid place-items-center p-4 sm:p-10"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Cerrar" className="absolute top-5 right-5 grid place-items-center w-12 h-12 text-white/80 hover:text-white hover:bg-white/10 transition-colors">
        <X size={26} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNav(-1); }} data-testid="lightbox-prev" aria-label="Anterior" className="absolute left-3 sm:left-6 grid place-items-center w-12 h-12 text-white/80 hover:text-white hover:bg-white/10 transition-colors">
        <ChevronLeft size={30} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNav(1); }} data-testid="lightbox-next" aria-label="Siguiente" className="absolute right-3 sm:right-6 grid place-items-center w-12 h-12 text-white/80 hover:text-white hover:bg-white/10 transition-colors">
        <ChevronRight size={30} />
      </button>
      <motion.div key={index} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={item.src} alt={item.label} className="w-full max-h-[80vh] object-contain" />
        <p className="text-center text-white/80 font-heading font-semibold mt-4">{item.label}</p>
      </motion.div>
    </motion.div>
  );
};

export const Gallery = () => {
  const withPhotos = GALLERY.map((g, i) => (g.src ? i : null)).filter((i) => i !== null);
  const [open, setOpen] = useState(null);

  const nav = (dir) => {
    const pos = withPhotos.indexOf(open);
    const next = withPhotos[(pos + dir + withPhotos.length) % withPhotos.length];
    setOpen(next);
  };

  return (
    <section id="galeria" className="bg-background py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-electric">Galería</span>
            <h2 className="font-heading font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-5">
              Proyectos que dejan<br />entrar la luz.
            </h2>
          </div>
          <a href={waUrl("Hola, me gustaría ver más ejemplos de vuestros trabajos.")} target="_blank" rel="noopener noreferrer" className="text-electric font-semibold hover:underline underline-offset-4" data-testid="gallery-more-link">
            Ver más trabajos por WhatsApp →
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[240px] gap-6">
          {GALLERY.map((item, i) => (
            <GalleryItem key={i} item={item} i={i} onOpen={setOpen} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && <Lightbox index={open} onClose={() => setOpen(null)} onNav={nav} />}
      </AnimatePresence>
    </section>
  );
};
